import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MarketingLayout } from '@/features/marketing/MarketingLayout';
import { Home } from '@/features/marketing/Home';
import { TrySample } from '@/features/marketing/TrySample';
import {
  Accessibility, CommunityGuidelines, Contact, ForParents, GalleryPublic, Help,
  HowItWorks, NotFound, Pricing, Privacy, StoriesIndex, StoryPreview, Terms,
} from '@/features/marketing/Pages';
import { AppSetup, AppWelcome, ChildShell, FamilyLayout } from '@/features/family/FamilyApp';
import { ParentGate } from '@/features/parent/ParentGate';
import {
  ParentChildDetail, ParentDashboard, ParentKeepsakes, ParentSettings, ParentShell,
} from '@/features/parent/ParentArea';
import { ChildGate } from '@/features/family/childContext';
import {
  ArtDone, ChildActivities, ChildBooks, ChildCreate, ChildHome, ChildReader,
  ChildStudio, MyArt, StoryDetail,
} from '@/features/family/ChildScreens';
import {
  EditionBuilder, EditionReaderScreen, WorldEditor,
} from '@/features/family/EditionScreens';

/**
 * Route map (spec §3).
 *
 * Three separate layouts as required by §2.2: the public site, the family app,
 * and later the admin console. Public routes are NOT nested inside an
 * authenticated shell, so a signed-out visitor always gets the marketing site
 * and the sample — the previous build was flagged for exactly that (§19.1).
 */
export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* ---------------- Public website (W01–W08) ---------------- */}
        <Route element={<MarketingLayout />}>
          <Route index element={<Home />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="stories" element={<StoriesIndex />} />
          <Route path="stories/:slug" element={<StoryPreview />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="for-parents" element={<ForParents />} />
          <Route path="gallery" element={<GalleryPublic />} />
          <Route path="help" element={<Help />} />
          <Route path="contact" element={<Contact />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="community-guidelines" element={<CommunityGuidelines />} />
          <Route path="accessibility" element={<Accessibility />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Full-screen sample. Never exposes public posting (spec §5 W08). */}
        <Route path="try" element={<TrySample />} />

        {/* ---------------- Family app (A01–C15) ---------------- */}
        <Route path="app" element={<FamilyLayout />}>
          <Route index element={<AppWelcome />} />
          <Route path="setup" element={<AppSetup />} />
          <Route path="choose-child" element={<Navigate to="/app" replace />} />
          {/* Every parent screen sits behind the adult gate (spec §6.3). */}
          <Route path="parent" element={<ParentGate><ParentShell /></ParentGate>}>
            <Route index element={<ParentDashboard />} />
            <Route path="children/:childId" element={<ParentChildDetail />} />
            <Route path="keepsakes" element={<ParentKeepsakes />} />
            <Route path="settings" element={<ParentSettings />} />
            <Route path="*" element={<Navigate to="/app/parent" replace />} />
          </Route>

          {/* Screens that keep the four child tabs. */}
          <Route path="child/:childId" element={<ChildShell />}>
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<ChildHome />} />
            <Route path="books" element={<ChildBooks />} />
            <Route path="books/:storyId" element={<StoryDetail />} />
            <Route path="create" element={<ChildCreate />} />
            <Route path="create/:storyId" element={<ChildCreate />} />
            <Route path="my-art" element={<MyArt />} />
            <Route path="art/:artworkId/done" element={<ArtDone />} />
            <Route path="editions/:storyId/build" element={<EditionBuilder />} />
            <Route path="world" element={<WorldEditor />} />
          </Route>

          {/* Immersive screens replace the global navigation with task
              controls (spec §3), so they sit outside ChildShell. */}
          <Route
            path="child/:childId/read/:storyId"
            element={<ChildGate><ChildReader /></ChildGate>}
          />
          <Route
            path="child/:childId/activities/:storyId"
            element={<ChildGate><ChildActivities /></ChildGate>}
          />
          <Route
            path="child/:childId/art/:artworkId/edit"
            element={<ChildGate><ChildStudio /></ChildGate>}
          />
          {/* The edition reader is immersive — it replaces the tabs. */}
          <Route
            path="child/:childId/editions/:editionId"
            element={<ChildGate><EditionReaderScreen /></ChildGate>}
          />
        </Route>

        {/* Legacy URLs from the previous build redirect rather than 404
            (spec §19.2 step 8). */}
        <Route path="Home" element={<Navigate to="/" replace />} />
        <Route path="parent-dashboard" element={<Navigate to="/app/parent" replace />} />
        <Route path="create-child" element={<Navigate to="/app/setup" replace />} />
        <Route path="child/:childId/*" element={<Navigate to="/app" replace />} />
      </Routes>
    </>
  );
}

/** Reset the reading position between route changes. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
